import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Person,
  TrendingUp,
  Refresh
} from '@mui/icons-material';
import axios from 'axios';
import { getAPIUrl } from '../apiUtils';

const API_BASE_URL = getAPIUrl();

const TopUsersStats = ({ selectedGroups, startDate, endDate, communities }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [userNames, setUserNames] = useState({});

  // Функция для получения названия сообщества по ID
  const getCommunityName = (groupId) => {
    if (!communities) return groupId;
    const community = communities.find(c => c.group_id === groupId);
    return community ? community.title : groupId;
  };

  // Функция для получения имен пользователей VK
  const fetchUserNames = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    try {
      const params = new URLSearchParams();
      userIds.forEach(id => params.append('user_ids', id));
      
      const response = await axios.get(
        `${API_BASE_URL}/vk/get_data_users/stored?${params.toString()}`
      );
      
      let data = response.data;
      if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (!Array.isArray(data)) {
        data = [];
      }
      
      const nameMap = {};
      data.forEach(user => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        const displayName = fullName || `ID: ${user.id}`;
        const screenName = user.screen_name ? ` (@${user.screen_name})` : '';
        nameMap[user.id] = displayName + screenName;
      });
      
      setUserNames(nameMap);
    } catch (err) {
      console.error('Ошибка при получении имен пользователей VK:', err);
      const fallbackMap = {};
      userIds.forEach(id => {
        fallbackMap[id] = `ID: ${id}`;
      });
      setUserNames(fallbackMap);
    }
  }, []);

  // Функция для получения топ пользователей
  const fetchTopUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      
     
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      params.append('limit', '20');

      const response = await axios.get(
        `${API_BASE_URL}/influencers/activity?${params.toString()}`
      );

      let data = response.data;
      if (response.data.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (!Array.isArray(data)) {
        data = [];
      }

      // Сортируем пользователей по количеству комментариев и берем топ 20
      const sortedUsers = data
        .sort((a, b) => (b.total_comments || 0) - (a.total_comments || 0))
        .slice(0, 20);

      setTopUsers(sortedUsers);

      // Загружаем имена пользователей для топ-20
      const userIds = sortedUsers.map(user => user.user_id);
      if (userIds.length > 0) {
        fetchUserNames(userIds);
      }

    } catch (err) {
      console.error('Ошибка при получении топ пользователей:', err);
      setError('Не удалось загрузить топ пользователей');
      setTopUsers([]);
    } finally {
      setLoading(false);
    }
  }, [selectedGroups, startDate, endDate, fetchUserNames]);

  // Загружаем данные при изменении параметров
  useEffect(() => {
    fetchTopUsers();
  }, [fetchTopUsers]);

  const handleRefresh = () => {
    fetchTopUsers();
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Заголовок с кнопкой обновления */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Person sx={{ color: '#00c853', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e1e2f' }}>
            Топ-20 пользователей по комментариям
          </Typography>
        </Box>
        <Tooltip title="Обновить данные">
          <IconButton onClick={handleRefresh} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Таблица топ пользователей */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Пользователь</TableCell>
              <TableCell align="right">Комментарии</TableCell>
              <TableCell align="right">Лайки</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topUsers.map((user, index) => (
              <TableRow key={user.user_id}>
                <TableCell>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    sx={{ 
                      backgroundColor: index < 3 ? '#ff9800' : '#e0e0e0',
                      color: index < 3 ? 'white' : 'black',
                      fontWeight: 'bold'
                    }} 
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {userNames[user.user_id] || `ID: ${user.user_id}`}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ID: {user.user_id}
                      </Typography>
                      <Chip 
                        label={getCommunityName(user.group_id)} 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#e3f2fd', 
                          color: '#1976d2',
                          fontSize: '0.7rem',
                          height: '20px'
                        }} 
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4a90e2' }}>
                    {user.total_comments?.toLocaleString() || 0}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ color: '#00c853' }}>
                    {user.total_likes?.toLocaleString() || 0}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TopUsersStats;
